/* eslint-disable no-nested-ternary */
import * as React from 'react';

import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import MonthSummary from '../Card/Dashboard/MonthSummary/MonthSummary';
import ImageCustomerSupport from '../../assets/images/image_customer_support.png';
import Icon from '../Icon/Icon';
import Graphic from '../Graphic/Graphic';
import { paths } from '../../routes';

type ModalProps = {
  user: string;
  currencySymbol: string;
};

function TourGuide({ user, currencySymbol }: ModalProps) {
  const navigate = useNavigate();

  return (
    <div className="fixed z-[40] inset-x-0 bottom-0 top-[60px] pb-[60px] overflow-hidden bg-[#f6f8fa] h-full">
      <div className="bg-[#f6f8fa] h-full overflow-auto pt-[25px] pb-[35px] px-8">
        <div>
          <h2 className="font-poppins font-semibold text-2xl text-pos-black">
            Hey, {user} 👋
            <Icon id="iconWelcomeEmoji" className="inline-flex" />
          </h2>
          <p className="text-lg mt-1">
            Swiftinflow talks to your store when you add your suppliers,
            products and make sales
          </p>
        </div>

        <div className="flex flex-row w-full gap-8 mt-6 flex-wrap tablet:flex-nowrap">
          <div className="w-full tablet:w-[70%] laptop:w-[75%]">
            <div className="relative w-full rounded-b-md rounded-t-lg border border-pos-dark-yellow">
              <div className="hidden tablet:block absolute bottom-10 left-0 w-[70%]">
                <img
                  src={ImageCustomerSupport}
                  alt="support"
                  className="h-auto w-[400px]"
                />

                <div
                  className="text-sm w-[400px] h-[200px] absolute top-[-60px] right-[60px] 
                  text-white"
                >
                  <Graphic
                    id="graphicTooltipSolidBg"
                    className="tour-tooltip-popup absolute top-0 scale-x-[-1] w-[400px] h-[200px]"
                  />
                  <div className="absolute top-[20px] p-5 w-full">
                    <h3 className="text-center text-lg">
                      Not sure what to do 🤔?
                    </h3>
                    <p className="text-sm mt-1 text-light">
                      Don&apos;t worry! We are here to help you to get your
                      store running as quick as possible.{' '}
                      <a
                        className="block text-center text-pos-yellow no-underline hover:underline underline-offset-2 text-lg mt-1"
                        href="tel:+233549961446"
                      >
                        Get Help
                      </a>
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3">
                <div>
                  <div
                    className="relative flex flex-row items-center px-7 py-5 bg-gradient-to-r from-pos-dark-yellow to-[#C7B98C] 
                    min-h-[80px] w-full rounded-tl-md text-lg text-white
                    after:content-[''] after:border-l-[15px] after:border-l-[#C7B98C]
                    border-r-[9px] border-white tour-header-arrow"
                  >
                    <Icon
                      id="iconSuppliersNav"
                      className="mr-3 relative items-center w-7 h-7 fill-white scale-x-[-1]"
                    />
                    Suppliers
                  </div>
                  <div className="mt-5 px-8">
                    <div>
                      <h3 className="font-semibold">Suppliers</h3>
                      <p className="text-sm mt-1">
                        Keep in touch with your suppliers and know what exactly
                        you buy.
                      </p>
                      <div className="mt-4 space-y-4 text-pos-hyperlink font-light text-xbase">
                        <button
                          onClick={() =>
                            navigate(
                              paths.ADD_MISC_URL_PATH.replace(
                                ':miscId',
                                'supplier',
                              ),
                            )
                          }
                          type="button"
                          className="flex flex-row items-center hover:underline underline-offset-2"
                        >
                          New Supplier
                          <Icon
                            id="plusSign"
                            className="ml-5 relative items-center w-3 h-3 fill-[#5c5f62]"
                          />
                        </button>
                        <button
                          onClick={() => navigate(paths.SUPPLIERS_URL_PATH)}
                          type="button"
                          className="flex flex-row items-center hover:underline underline-offset-2"
                        >
                          Manage Suppliers
                          <Icon
                            id="arrowNext"
                            className="ml-5 relative items-center w-3 h-3 fill-[#5c5f62]"
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <div
                    className="relative flex flex-row items-center pl-14 pr-7 py-5 w-full bg-gradient-to-r from-pos-dark-yellow to-[#C7B98C]
                    text-lg text-white 
                    min-h-[80px] after:content-[''] after:border-l-[15px] after:border-l-[#C7B98C]
                    border-r-[9px] border-white tour-header-arrow"
                  >
                    <Icon
                      id="iconProductNav"
                      className="mr-3 relative items-center w-6 h-6 fill-white"
                    />
                    Inventory
                  </div>
                  <div className="mt-5 px-8">
                    <div>
                      <h3 className="font-semibold">Product</h3>
                      <p className="text-sm mt-1">
                        Build out your inventory so you can start selling
                        products with Swiftinflow.
                      </p>
                      <div className="mt-4 space-y-4 text-pos-hyperlink font-light text-xbase">
                        <button
                          onClick={() =>
                            navigate(
                              paths.ADD_MISC_URL_PATH.replace(
                                ':miscId',
                                'inventory',
                              ),
                            )
                          }
                          type="button"
                          className="flex flex-row items-center hover:underline underline-offset-2"
                        >
                          New Product
                          <Icon
                            id="plusSign"
                            className="ml-5 relative items-center w-3 h-3 fill-[#5c5f62]"
                          />
                        </button>
                        <button
                          onClick={() => navigate(paths.INVENTORIES_URL_PATH)}
                          type="button"
                          className="flex flex-row items-center hover:underline underline-offset-2"
                        >
                          Product List
                          <Icon
                            id="arrowNext"
                            className="ml-5 relative items-center w-3 h-3 fill-[#5c5f62]"
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <div
                    className="relative flex flex-row items-center min-h-[80px] pl-14 pr-7 py-5 bg-gradient-to-r from-pos-dark-yellow to-[#C7B98C]
                    w-full rounded-tr-md text-lg text-white"
                  >
                    <Icon
                      id="iconPOSNav"
                      className="mr-3 relative items-center w-6 h-6 fill-white"
                    />
                    Sales
                  </div>
                  <div className="pt-5 pb-10 px-8 flex flex-col space-y-5 divide-y">
                    <div>
                      <h3 className="font-semibold">Sales</h3>
                      <p className="text-sm mt-1">
                        Get an accurate report on your sales performance by
                        adding cash registers, customers, etc
                      </p>
                      <div className="mt-4 space-y-4 text-pos-hyperlink font-light text-xbase">
                        <button
                          onClick={() => navigate(paths.POS_TELLER_URL_PATH)}
                          type="button"
                          className="flex flex-row items-center hover:underline underline-offset-2"
                        >
                          Make a Sale
                          <Icon
                            id="plusSign"
                            className="ml-5 relative items-center w-3 h-3 fill-[#5c5f62]"
                          />
                        </button>
                        <button
                          onClick={() => navigate(paths.POS_TELLER_URL_PATH)}
                          type="button"
                          className="flex flex-row items-center hover:underline underline-offset-2"
                        >
                          Sales List
                          <Icon
                            id="arrowNext"
                            className="ml-5 relative items-center w-3 h-3 fill-[#5c5f62]"
                          />
                        </button>
                      </div>
                    </div>

                    <div className="pt-5">
                      <h3 className="font-semibold text-xbase">
                        Cash Registers
                      </h3>

                      <div className="mt-4 space-y-4 text-pos-hyperlink font-light text-xbase">
                        <button
                          onClick={() =>
                            navigate(
                              paths.PREFERENCES_URL_PATH.replace(
                                ':tabId',
                                'registers',
                              ),
                            )
                          }
                          type="button"
                          className="flex flex-row items-center hover:underline underline-offset-2"
                        >
                          New Register
                          <Icon
                            id="plusSign"
                            className="ml-5 relative items-center w-3 h-3 fill-[#5c5f62]"
                          />
                        </button>
                        <button
                          onClick={() =>
                            navigate(
                              paths.PREFERENCES_URL_PATH.replace(
                                ':tabId',
                                'registers',
                              ),
                            )
                          }
                          type="button"
                          className="flex flex-row items-center hover:underline underline-offset-2"
                        >
                          List of Cash Registers
                          <Icon
                            id="arrowNext"
                            className="ml-5 relative items-center w-3 h-3 fill-[#5c5f62]"
                          />
                        </button>
                      </div>
                    </div>

                    <div className="pt-5">
                      <h3 className="font-semibold text-xbase">Customers</h3>
                      <div className="mt-4 space-y-4 text-pos-hyperlink font-light text-xbase">
                        <button
                          onClick={() =>
                            navigate(
                              paths.ADD_MISC_URL_PATH.replace(
                                ':miscId',
                                'customer',
                              ),
                            )
                          }
                          type="button"
                          className="flex flex-row items-center hover:underline underline-offset-2"
                        >
                          New Customer
                          <Icon
                            id="plusSign"
                            className="ml-5 relative items-center w-3 h-3 fill-[#5c5f62]"
                          />
                        </button>
                        <button
                          onClick={() => navigate(paths.CUSTOMERS_URL_PATH)}
                          type="button"
                          className="flex flex-row items-center hover:underline underline-offset-2"
                        >
                          Manage Customers
                          <Icon
                            id="arrowNext"
                            className="ml-5 relative items-center w-3 h-3 fill-[#5c5f62]"
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="w-full tablet:w-[30%] laptop:w-[25%] order-1 tablet:order-2">
            <MonthSummary
              onClick={() => navigate(paths.POS_TELLER_URL_PATH)}
              tourGuide
              currencySymbol={currencySymbol}
              monthSummary={{
                month: moment().format('MMMM'),
                sales: 0,
                salesCount: 0,
                cost: 0,
                profit: 0,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

TourGuide.defaultProps = {};

export default TourGuide;
